import React, { useRef, useEffect } from 'react';

function ScrollVideo({ src, poster }) {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      src={src ? `${src}#t=0,50` : undefined}
      poster={poster || undefined}
      muted
      autoPlay
      loop
      playsInline
      preload="auto"
      style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
    />
  );
}

export default function Projects({ t }) {
  return (
    <section id="projects" className="bg-bg-soft border-t border-rule">
      <div className="max-w-content mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="mb-10 md:mb-14">
          <div className="font-mono text-[13px] tracking-eyebrow uppercase text-ink-3">
            01 / {t.nav.projects}
          </div>
        </div>

        <div className="flex flex-col gap-16 md:gap-24">
          {t.projects.items.map((p, i) => {
            const imageRight = i % 2 === 0;

            const Imagery = (
              <div
                className="border border-rule overflow-hidden relative"
                style={{
                  aspectRatio: '16/11',
                  borderRadius: 16,
                  background: '#F4F7FB',
                  backgroundImage:
                    'repeating-linear-gradient(135deg, rgba(26,34,56,0.04) 0 1px, transparent 1px 14px)',
                }}
              >
                {p.media?.type === 'video' ? (
                  <ScrollVideo src={p.media.src} poster={p.media.poster} />
                ) : p.media?.type === 'image' ? (
                  <img
                    src={p.media.src}
                    alt={p.media.alt || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div
                    className="w-full h-full bg-bg flex items-center justify-center text-ink-3 font-mono text-xs tracking-eyebrow"
                    aria-label={`Project ${i + 1} image`}
                  />
                )}
              </div>
            );

            const Copy = (
              <div>
                {p.company && (
                  <div className="font-mono text-xs tracking-[0.1em] uppercase text-ink-3 mb-4">
                    {p.company}
                  </div>
                )}
                <h3
                  className="font-display font-medium text-ink m-0 mb-5"
                  style={{ fontSize: 'clamp(24px, 2.4vw, 36px)', lineHeight: 1.15, letterSpacing: '-0.02em' }}
                >
                  {p.title}
                </h3>
                <p className="leading-relaxed text-ink-2 m-0 mb-7" style={{ fontSize: 16, maxWidth: 520 }}>
                  {p.body}
                </p>
                <a
                  href={p.learnMoreHref}
                  target={p.learnMoreHref !== '#' ? '_blank' : undefined}
                  rel={p.learnMoreHref !== '#' ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center gap-2 bg-ink text-bg no-underline font-medium text-[15px] mb-9 transition-opacity duration-150 hover:opacity-80"
                  style={{ padding: '12px 22px', borderRadius: 10 }}
                >
                  {p.learnMore} <span className="font-mono">→</span>
                </a>

                {p.metrics && p.metrics.length > 0 && (
                  <div
                    className="grid gap-7 pt-6 border-t border-rule"
                    style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: 520 }}
                  >
                    {p.metrics.map((m, j) => (
                      <div key={j} className="flex gap-3.5 items-start">
                        <div className="w-1 self-stretch bg-accent rounded-sm shrink-0" />
                        <div>
                          <div
                            className="font-display font-medium text-ink mb-1.5"
                            style={{ fontSize: 28, lineHeight: 1, letterSpacing: '-0.025em' }}
                          >
                            {m.value}
                          </div>
                          <div className="text-[13px] text-ink-2 leading-snug">{m.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );

            return (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center"
              >
                {/* Mobile: always imagery on top. Desktop: alternate. */}
                <div className={imageRight ? 'order-1 md:order-2' : 'order-1 md:order-1'}>
                  {Imagery}
                </div>
                <div className={imageRight ? 'order-2 md:order-1' : 'order-2 md:order-2'}>
                  {Copy}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
